/**
 * ComplianceDashboard Component
 * Compliance tab orchestrator: summary cards + collapsible detail sections
 */

import { MetricCard } from './MetricCard';
import { BASForecastSection } from './BASForecastSection';
import { LeaveProvisionsSection } from './LeaveProvisionsSection';
import { SuperEmployeeTable } from './SuperEmployeeTable';
import { CommentButton } from './CommentButton';
import { AIInsightSection } from './AIInsightSection';
import { PageNotes } from './PageNotes';
import { useCompliance } from '@/hooks/useCompliance';
import { formatCurrency } from '@/utils/formatters';
import './ComplianceDashboard.css';

export function ComplianceDashboard() {
  const { data, loading, error, refresh } = useCompliance();

  if (error) {
    return (
      <div className="dashboard-error" role="alert" aria-live="assertive">
        <div className="dashboard-error__icon">&#9888;</div>
        <h2 className="dashboard-error__title">Unable to load Compliance data</h2>
        <p className="dashboard-error__message">{error}</p>
        <button onClick={refresh} className="dashboard-error__button" aria-label="Retry">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="compliance-dashboard">
      <header className="compliance-dashboard__header">
        <div>
          <h1 className="compliance-dashboard__title">Compliance</h1>
          <p className="compliance-dashboard__subtitle">
            Australian regulatory obligations
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="dashboard-header__refresh"
          aria-label="Refresh compliance data"
        >
          <span className={loading ? 'loading-spinner' : ''}>&#8635;</span>
          Refresh
        </button>
      </header>

      {/* Summary Cards */}
      <div className="compliance-dashboard__cards">
        <MetricCard
          title="GST Net"
          value={formatCurrency(data?.gstPosition?.netGST ?? 0)}
          headerAction={<CommentButton targetType="metric" targetId="gst-net" />}
          subtitle={
            data?.gstPosition
              ? `${data.gstPosition.fromDate} to ${data.gstPosition.toDate}`
              : undefined
          }
          icon="&#128200;"
          loading={loading}
          variant={(data?.gstPosition?.netGST ?? 0) > 0 ? 'warning' : 'success'}
        >
          {data?.gstPosition && (
            <div className="breakdown">
              <div className="breakdown__item">
                <span className="breakdown__label">Collected</span>
                <span className="breakdown__value">
                  {formatCurrency(data.gstPosition.gstCollected)}
                </span>
              </div>
              <div className="breakdown__item">
                <span className="breakdown__label">Paid</span>
                <span className="breakdown__value">
                  {formatCurrency(data.gstPosition.gstPaid)}
                </span>
              </div>
            </div>
          )}
        </MetricCard>

        <MetricCard
          title="Tax Provision"
          value={formatCurrency(data?.taxProvision?.estimatedTax ?? 0)}
          headerAction={<CommentButton targetType="metric" targetId="tax-provision" />}
          subtitle={data?.taxProvision
            ? data.taxProvision.financialYear.startsWith('FY')
              ? data.taxProvision.financialYear
              : `FY ${data.taxProvision.financialYear}`
            : undefined
          }
          icon="&#127970;"
          loading={loading}
        >
          {data?.taxProvision && (
            <div className="breakdown">
              <div className="breakdown__item">
                <span className="breakdown__label">Tax Rate</span>
                <span className="breakdown__value">
                  {(data.taxProvision.taxRate * 100).toFixed(0)}%
                </span>
              </div>
              <div className="breakdown__item">
                <span className="breakdown__label">Taxable Income</span>
                <span className="breakdown__value">
                  {formatCurrency(data.taxProvision.taxableIncome)}
                </span>
              </div>
            </div>
          )}
        </MetricCard>

        <MetricCard
          title="Super Obligations"
          value={formatCurrency(data?.superSummary?.totalSuper ?? 0)}
          headerAction={<CommentButton targetType="metric" targetId="super-obligations" />}
          subtitle={data?.superSummary ? `${data.superSummary.period}` : undefined}
          icon="&#128101;"
          loading={loading}
          variant={(data?.superSummary?.totalSuper ?? 0) > 0 ? 'warning' : 'default'}
        >
          {data?.superSummary && (
            <div className="breakdown">
              <div className="breakdown__item">
                <span className="breakdown__label">Rate</span>
                <span className="breakdown__value">
                  {(data.superSummary.rate * 100).toFixed(1)}%
                </span>
              </div>
              <div className="breakdown__item">
                <span className="breakdown__label">Employees</span>
                <span className="breakdown__value">
                  {data.superSummary.employeeCount ?? data.superSummary.employees.length}
                </span>
              </div>
            </div>
          )}
        </MetricCard>
      </div>

      {/* Collapsible Sections */}
      <div className="compliance-dashboard__sections">
        {data?.basForecast && (
          <BASForecastSection basForecast={data.basForecast} />
        )}
        {data?.leaveProvisions && (
          <LeaveProvisionsSection leaveProvisions={data.leaveProvisions} />
        )}
        {data?.superSummary && (
          <SuperEmployeeTable employees={data.superSummary.employees} />
        )}
        <AIInsightSection
          id="compliance"
          title="AI Analysis — Compliance"
          question="What are our current Australian tax and compliance obligations? Summarise GST payable, estimated income tax, super obligations, and any upcoming BAS deadlines."
          contextHint="compliance"
        />
        <PageNotes page="compliance" title="Compliance Notes" />
      </div>
    </div>
  );
}
